// @ts-nocheck
import type { ActiveSidebarContext } from '../contexts/SidebarContext';

// URL pattern to context mapping
export const URL_CONTEXT_MAP: Record<string, ActiveSidebarContext> = {
  '/organization': 'organization',
  '/settings': 'settings',
  '/help': 'help',
  '/image': 'image',
  '/multi': 'multi',
  '/video': 'video',
  '/audio': 'audio',
  '/text': 'text',
  '/3d': '3d',
  '/gallery': 'gallery',
  '/': 'image'
};

// More complex pattern matching for nested routes
export const URL_PATTERN_CONTEXT_MAP: Array<{
  pattern: RegExp;
  context: ActiveSidebarContext;
}> = [
  {
    pattern: /^\/admin/,
    context: 'admin'
  },
  {
    pattern: /^\/organization/,
    context: 'organization'
  },
  {
    pattern: /^\/gallery/,
    context: 'gallery'
  },
  {
    pattern: /^\/video/,
    context: 'video'
  },
  {
    pattern: /^\/audio/,
    context: 'audio'
  },
  {
    pattern: /^\/text/,
    context: 'text'
  },
  {
    pattern: /^\/3d/,
    context: '3d'
  },
  {
    pattern: /^\/multi/,
    context: 'multi'
  },
  {
    pattern: /^\/image/,
    context: 'image'
  },
  {
    pattern: /^\/$/,
    context: 'image'
  }
];

/**
 * Determines the appropriate sidebar context based on current URL pathname
 */
export function getContextFromUrl(pathname: string): ActiveSidebarContext | null {
  // Try exact match first
  if (URL_CONTEXT_MAP[pathname]) {
    return URL_CONTEXT_MAP[pathname];
  }

  // Try pattern matching
  for (const { pattern, context } of URL_PATTERN_CONTEXT_MAP) {
    if (pattern.test(pathname)) {
      return context;
    }
  }

  return null;
}

/**
 * Default paths for each context when no sub-path is saved
 */
export const DEFAULT_CONTEXT_PATHS: Record<ActiveSidebarContext, string> = {
  admin: '/admin/users',
  organization: '/organization/info',
  image: '/image',
  multi: '/multi',
  gallery: '/gallery',
  video: '/video',
  audio: '/audio',
  text: '/text',
  '3d': '/3d',
  settings: '/settings',
  help: '/help'
};

/**
 * Check if a context supports internal navigation (has sub-pages)
 */
export function hasSubNavigation(context: ActiveSidebarContext): boolean {
  return ['admin', 'organization'].includes(context);
}

/**
 * Get the best navigation path for a context
 * Uses saved sub-context state or falls back to default
 */
export function getBestPathForContext(
  context: ActiveSidebarContext,
  savedSubPath: string | null,
  currentPath: string
): string {
  // If it's a context with sub-navigation and we have a saved path, validate it first
  if (hasSubNavigation(context) && savedSubPath && savedSubPath !== currentPath) {
    // Special case: if saved path is '/admin' (old dashboard), use new default
    if (context === 'admin' && savedSubPath === '/admin') {
      return DEFAULT_CONTEXT_PATHS[context];
    }
    return savedSubPath;
  }

  // Otherwise use default path
  return DEFAULT_CONTEXT_PATHS[context];
}

/**
 * Validate that a path belongs to a specific context
 */
export function isPathInContext(path: string, context: ActiveSidebarContext): boolean {
  switch (context) {
    case 'admin':
      return path.startsWith('/admin');
    case 'organization':
      return path.startsWith('/organization');
    case 'gallery':
      // Gallery context is active only when explicitly on gallery route
      return path === '/gallery' || path.startsWith('/gallery/');
    case 'video':
      // Video context is active only when explicitly on video route
      return path === '/video' || path.startsWith('/video/');
    case 'audio':
      // Audio context is active only when explicitly on audio route
      return path === '/audio' || path.startsWith('/audio/');
    case 'text':
      // Text context is active only when explicitly on text route
      return path === '/text' || path.startsWith('/text/');
    case '3d':
      // 3D context is active only when explicitly on the 3d route
      return path === '/3d' || path.startsWith('/3d/');
    case 'multi':
      // Multi context is active only when explicitly on multi route
      return path === '/multi' || path.startsWith('/multi/');
    case 'image':
      // Image context is active on main page or /image route
      return path === '/' || path === '/image' || path.startsWith('/image/');
    case 'settings':
      return path === '/settings' || path.startsWith('/settings/');
    case 'help':
      return path === '/help' || path.startsWith('/help/');
    default:
      return false;
  }
}

/**
 * Extract sub-context identifier from URL
 * For example: '/admin/users' -> 'users'
 */
export function getSubContextFromUrl(pathname: string, context: ActiveSidebarContext): string | null {
  switch (context) {
    case 'admin':
      const adminMatch = pathname.match(/^\/admin(?:\/(.+))?$/);
      return adminMatch?.[1] || null;
    
    case 'organization':
      const orgMatch = pathname.match(/^\/organization(?:\/(.+))?$/);
      return orgMatch?.[1] || null;
    
    default:
      return null;
  }
}